import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { PageHeader } from "@/components/PageHeader";
import { ORDER_CATEGORY_LABEL, ORDER_STATUS_LABEL, fmtDate } from "@/lib/format";
import { ArrowLeft, FileText, CheckCircle2, Award, AlertTriangle } from "lucide-react";

export default function ServiceOrderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [checklist, setChecklist] = useState([]);
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");

  const load = async () => {
    const r = await api.get(`/service-orders/${id}`);
    setOrder(r.data);
    setChecklist(r.data.checklist || []);
    setNotes(r.data.notes || "");
  };

  useEffect(() => { load(); }, [id]);

  const isWorkshop = user.role === "workshop_admin" || user.role === "superadmin";
  const editable = isWorkshop && order && order.status !== "completed";

  const saveProgress = async () => {
    setError("");
    try {
      await api.put(`/service-orders/${id}`, { checklist, notes, status: "in_progress" });
      load();
    } catch (e) {
      setError(e.response?.data?.detail || "Error");
    }
  };

  const closeOrder = async () => {
    setError("");
    try {
      await api.put(`/service-orders/${id}`, { checklist, notes, status: "completed" });
      load();
    } catch (e) {
      setError(e.response?.data?.detail || "Error");
    }
  };

  if (!order) return <div className="p-8">Cargando...</div>;
  const allChecked = checklist.length > 0 && checklist.every((c) => c.checked);

  return (
    <div data-testid="order-detail-page">
      <Link to="/taller" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-600 mb-4">
        <ArrowLeft size={14} strokeWidth={2.5} /> Volver al taller
      </Link>

      <PageHeader
        tag={`Orden ${id.slice(0, 8)}`}
        title={`${ORDER_CATEGORY_LABEL[order.category]}`}
        description={`Patente ${order.vehicle?.plate || "-"} · ${order.vehicle?.brand || ""} ${order.vehicle?.model || ""}`}
        action={
          <span className={`text-xs font-bold uppercase tracking-wider px-3 py-2 ${order.status === "completed" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`} data-testid="order-status">
            {ORDER_STATUS_LABEL[order.status]}
          </span>
        }
      />

      <div className="grid lg:grid-cols-2 gap-px bg-gray-200 border border-gray-200 mb-8">
        <div className="bg-white p-6">
          <p className="veta-tag">Descripción</p>
          <p className="mt-2 text-sm">{order.description}</p>
          <p className="veta-tag mt-6">Kilometraje</p>
          <p className="mt-1 font-mono text-2xl">{(order.mileage || 0).toLocaleString("es-CL")} km</p>
          <p className="veta-tag mt-6">Fecha</p>
          <p className="mt-1 text-sm">{fmtDate(order.created_at)}</p>
        </div>
        <div className="bg-white p-6">
          <p className="veta-tag mb-3">Acciones</p>
          <div className="space-y-3">
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/pdf/service-order/${id}`} target="_blank" rel="noreferrer" className="w-full h-touch flex items-center justify-center gap-2 border-2 border-black text-sm font-bold uppercase tracking-wider hover:bg-black hover:text-white transition-colors" data-testid="download-order-pdf">
              <FileText size={16} strokeWidth={2.5} /> Descargar Orden (PDF + QR)
            </a>
            {order.status === "completed" && (
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/pdf/maintenance-cert/${id}`} target="_blank" rel="noreferrer" className="w-full h-touch flex items-center justify-center gap-2 bg-blue-600 text-white text-sm font-bold uppercase tracking-wider hover:bg-blue-700 transition-colors" data-testid="download-cert-pdf">
                <Award size={16} strokeWidth={2.5} /> Certificado de Mantención
              </a>
            )}
            {(order.overcharge_days || 0) > 0 && (user.role === "rentacar_admin" || user.role === "superadmin") && (
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/pdf/overcharge-notice/${id}`} target="_blank" rel="noreferrer" className="w-full h-touch flex items-center justify-center gap-2 bg-red-600 text-white text-sm font-bold uppercase tracking-wider hover:bg-red-700 transition-colors" data-testid="download-overcharge-pdf">
                <AlertTriangle size={16} strokeWidth={2.5} /> Notificación de Sobrecargo
              </a>
            )}
          </div>
        </div>
      </div>

      {(order.overcharge_days || 0) > 0 && (
        <div className="border-2 border-red-600 bg-red-50 p-6 mb-8" data-testid="overcharge-banner">
          <div className="flex items-start gap-4">
            <AlertTriangle size={32} strokeWidth={2.5} className="text-red-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="veta-tag text-red-800">Mantención antes del plazo pactado</p>
              <h3 className="font-display text-2xl text-red-700 mt-1">Sobrecargo aplicable</h3>
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="veta-tag">Última mantención</p>
                  <p className="font-mono mt-1 text-sm">{fmtDate(order.last_maintenance_at)}</p>
                </div>
                <div>
                  <p className="veta-tag">Días transcurridos</p>
                  <p className="font-mono mt-1 text-sm">{order.interval_days} d</p>
                </div>
                <div>
                  <p className="veta-tag">Mínimo pactado</p>
                  <p className="font-mono mt-1 text-sm">{order.min_days_between_maintenance} d</p>
                </div>
                <div>
                  <p className="veta-tag text-red-800">Sobrecargo</p>
                  <p className="font-display text-xl text-red-700 mt-1">{order.overcharge_days} días · ${Number(order.overcharge_amount).toLocaleString("es-CL")}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="border border-gray-200">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <p className="veta-tag">Checklist Técnico</p>
          <p className="text-xs text-gray-500">{checklist.filter((c) => c.checked).length}/{checklist.length} completado</p>
        </div>
        <div className="divide-y divide-gray-200">
          {checklist.map((item, i) => (
            <label key={i} className={`p-4 flex items-center gap-3 cursor-pointer ${item.checked ? "bg-green-50" : ""}`} data-testid={`checklist-item-${i}`}>
              <input
                type="checkbox"
                disabled={!editable}
                checked={item.checked}
                onChange={(e) => setChecklist(checklist.map((x, idx) => idx === i ? { ...x, checked: e.target.checked } : x))}
                className="w-5 h-5"
              />
              <span className="flex-1 text-sm">{item.label}</span>
              {item.checked && <CheckCircle2 size={18} className="text-green-600" strokeWidth={2.5} />}
            </label>
          ))}
        </div>
      </div>

      <div className="mt-6">
        <p className="veta-tag mb-2">Notas técnicas</p>
        <textarea
          disabled={!editable}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={4}
          className="w-full px-3 py-3 border border-gray-300 disabled:bg-gray-50"
          data-testid="order-notes"
        />
      </div>

      {error && <div className="status-danger p-3 mt-4 text-sm">{error}</div>}

      {editable && (
        <div className="mt-6 flex flex-wrap gap-3">
          <button onClick={saveProgress} className="h-touch px-6 border-2 border-black text-sm font-bold uppercase tracking-wider hover:bg-black hover:text-white transition-colors" data-testid="save-order-progress">
            Guardar Avance
          </button>
          <button
            onClick={closeOrder}
            disabled={!allChecked}
            className="h-touch px-6 bg-blue-600 text-white text-sm font-bold uppercase tracking-wider hover:bg-blue-700 disabled:opacity-50 transition-colors"
            data-testid="close-order-btn"
          >
            Cerrar Orden (Completar)
          </button>
          {!allChecked && <p className="w-full text-xs text-gray-500">Marca todos los items del checklist para poder cerrar la orden.</p>}
        </div>
      )}
    </div>
  );
}
